import { useWindowSize } from "hooks/useWindowSize";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { RightArrow } from "components/icons/RightArrow";
import { InvoiceItemSelector } from "components/InvoiceItemSelector/InvoiceItemSelector";
import { NumericInput } from "components/NumericInput/NumericInput";
import { Form, Formik, FormikProps } from "formik";
import { useRef, useState } from "react";
import { ItemWrapper } from "./ItemWrapper";
import { Button } from "components/DesignSystem/Button/Button";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { PRODUCTS_AND_SERVICES } from "constants/subscriptionPermissionFeatures";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { InvoiceItemValidation } from "formValidations/invoiceValidation";
import { useGetCategoryByIdentifierQuery } from "store/apis/chartOfAccounts";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { CheckboxField } from "components/DesignSystem/Checkbox/CheckboxField";
import classNames from "classnames";
import { InvoiceItemType } from "components/InvoiceForm/InvoiceForm";
import {
  CategoryData,
  useGetBookkeepingSettingsQuery,
} from "store/apis/booksLockDate";
import {
  OPERATING_EXPENSES_CATEGORY,
  SALES_AND_PRODUCTS_CATEGORY,
} from "constants/categoryIdentifier";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { ClassificationAssignment } from "components/ClassificationAssignment/ClassificationAssignment";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { Class } from "types/Models/classification";
import { PriceDropdown } from "./PriceDropdown";
import { TaxRateCombobox } from "components/TaxRateCombobox/TaxRateCombobox";
import { SalesTaxCodeCombobox } from "components/SalesTaxCodeCombobox/SalesTaxCodeCombobox";
import { useToast } from "hooks/useToast";
import {
  useAddCustomerDefaultPriceMutation,
  useGetCustomerDefaultPricesQuery,
  useGetInvoiceCustomerQuery,
} from "store/apis/invoices";
import {
  useGetProductAndServiceByIdQuery,
  useLazyGetProductAndServiceByIdQuery,
} from "store/apis/productAndServices";
import { ProductAndServicesDetail } from "types/Models/productAndServices";
import { BackendError } from "types/utils/error";
import { Info } from "components/icons/Info";
import { colors } from "constants/colors";
import { useLineItemTaxCompliance } from "components/SalesTax/useLineItemTaxCompliance";
import { LineItemTaxCompliance } from "components/SalesTax/LineItemTaxCompliance";

type FormInvoiceItem = InvoiceItemType & {
  is_public_bill_item?: boolean;
  classification_ids?: string[];
  price_id?: string | null;
  tax_rate_id?: string | null;
  product_code?: string | null;
  system_tax_rate?: number | null;
};

type ParentFormValues = {
  invoice_items?: InvoiceItemType[];
};

type LineItemAddress = {
  street_address: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

type LineItemUIProps = {
  invoice_item: InvoiceItemType;
  groupId: string;
  entityId: string;
  currency?: string;
  closeModal: () => void;
  onSave?: (item: FormInvoiceItem) => void;
  isUpdating?: boolean;
  rootCategoryIdentifiers: string[];
  showTextField?: boolean;
  showCategory?: boolean;
  isBillLineItem?: boolean;
  customerId?: string;
  invoiceDate?: string | null;
  showTaxField?: boolean;
  sellerAddress?: LineItemAddress;
  buyerAddress?: LineItemAddress;
  onEditCustomerAddress?: (customerId: string) => void;
};

const checkHasDuplicateClassification = ({
  values,
  invoice_item,
  existingInvoiceItems,
  allClassifications,
}: {
  values: FormInvoiceItem;
  invoice_item: InvoiceItemType;
  existingInvoiceItems: InvoiceItemType[];
  allClassifications: any[];
}) => {
  const hasDuplicateClassification =
    values.category_id &&
    values.classification_ids &&
    values.classification_ids.length > 0 &&
    existingInvoiceItems.some((item: InvoiceItemType) => {
      if (
        item.uuid === invoice_item.uuid ||
        item.category_id !== values.category_id ||
        !item.classification ||
        item.classification.length === 0
      ) {
        return false;
      }

      return item.classification.some((existingGroup) => {
        const existingGroupUuid = existingGroup.uuid;
        const existingClassUuids = existingGroup.classes.map(
          (cls: Class) => cls.uuid
        );

        const selectedClassesInThisGroup =
          values.classification_ids?.filter((classUuid) => {
            const classGroup = allClassifications.find((group) =>
              group.classes.some((cls: Class) => cls.uuid === classUuid)
            );
            return classGroup?.uuid === existingGroupUuid;
          }) || [];

        if (selectedClassesInThisGroup.length === 0) return false;

        const areClassesIdentical =
          selectedClassesInThisGroup.length === existingClassUuids.length &&
          existingClassUuids.every((uuid) =>
            selectedClassesInThisGroup.includes(uuid)
          );

        return !areClassesIdentical;
      });
    });

  return hasDuplicateClassification;
};

const SetDefaultPriceBanner = ({
  productId,
  priceId,
  rate,
  customerId,
  entityId,
  groupId,
}: {
  productId: string | null;
  priceId: string | null | undefined;
  rate: number;
  customerId?: string;
  entityId: string;
  groupId: string;
}) => {
  const { alertToast, successToast } = useToast();

  const { data: customerDefaultPrices = [] } = useGetCustomerDefaultPricesQuery(
    { entityId, customerId: customerId! },
    { skip: !entityId || !customerId }
  );

  const { data: productDetail } = useGetProductAndServiceByIdQuery(
    { productId: productId!, entityId, groupId },
    { skip: !productId || !entityId || !groupId }
  );

  const { data: customerDetail } = useGetInvoiceCustomerQuery(
    { groupId, entityId, customerId: customerId! },
    { skip: !entityId || !customerId }
  );

  const [addCustomerDefaultPrice, { isLoading: isAddingDefaultPrice }] =
    useAddCustomerDefaultPriceMutation();

  const customerDefaultPrice = customerDefaultPrices.find(
    (cdp) => cdp.product_id === productId
  );

  const linkedPrices = productDetail?.linked_price_list || [];
  const productDefaultPrice = linkedPrices.find((price) => price.is_default);

  const effectivePriceId =
    priceId ||
    customerDefaultPrice?.product_price.uuid ||
    productDefaultPrice?.uuid;

  const productName = productDetail?.name;
  const customerName = customerDetail?.name;
  const existingDefaultPrice = customerDefaultPrice?.product_price.price;

  const handleSetAsDefaultPrice = async () => {
    if (!effectivePriceId || !productId || !customerId) return;

    try {
      await addCustomerDefaultPrice({
        entityId,
        customerId,
        priceId: effectivePriceId,
        productId,
      }).unwrap();
      const formattedRate = rate.toFixed(2);
      successToast({
        message: `$${formattedRate} is set as default price for ${productName} for ${customerName}.`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const updatePrice =
    priceId &&
    customerDefaultPrice?.product_price.uuid &&
    priceId !== customerDefaultPrice?.product_price.uuid;

  const setPrice = priceId && !customerDefaultPrice?.product_price.uuid;

  if (updatePrice) {
    return (
      <Banner
        type="info"
        icon={<Info color={colors.blue[80]} stroke="1.2" size="18" />}
        description={
          <>
            Update the default price for the customer from{" "}
            <AmountSuperScript amount={existingDefaultPrice!} /> to{" "}
            <AmountSuperScript amount={rate} />?
          </>
        }
        cta={
          <Button
            size="small"
            customType="secondary"
            onClick={handleSetAsDefaultPrice}
            isLoading={isAddingDefaultPrice}
            disabled={isAddingDefaultPrice}
          >
            Update default price
          </Button>
        }
      />
    );
  }
  if (setPrice) {
    return (
      <Banner
        type="info"
        icon={<Info color={colors.blue[80]} stroke="1.2" size="18" />}
        description={
          <>
            Set <AmountSuperScript amount={rate} /> as default price for this
            customer?
          </>
        }
        cta={
          <Button
            size="small"
            customType="secondary"
            onClick={handleSetAsDefaultPrice}
            isLoading={isAddingDefaultPrice}
            disabled={isAddingDefaultPrice}
          >
            Set as default price
          </Button>
        }
      />
    );
  }

  return null;
};

export const LineItemUI = ({
  invoice_item,
  groupId,
  entityId,
  currency,
  closeModal,
  onSave,
  isUpdating = false,
  rootCategoryIdentifiers,
  showTextField = false,
  showCategory = true,
  isBillLineItem = false,
  showTaxField = false,
  invoiceDate,
  customerId,
  sellerAddress,
  buyerAddress,
  onEditCustomerAddress,
}: LineItemUIProps) => {
  const { isMobile } = useWindowSize();
  const parentFormikContext = useTypedFormikContext<ParentFormValues>();
  const existingInvoiceItems = parentFormikContext?.values?.invoice_items || [];

  const permission = useHasSubscriptionPermission({
    feature: PRODUCTS_AND_SERVICES,
  });

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data: categoryData } = useGetCategoryByIdentifierQuery({
    groupId,
    entityId,
    identifiers: rootCategoryIdentifiers,
  });

  const { data: settingsData } = useGetBookkeepingSettingsQuery({ entityId });

  const { data: customerDefaultPrices = [] } = useGetCustomerDefaultPricesQuery(
    { entityId, customerId: customerId! },
    { skip: !entityId || !customerId }
  );

  const [fetchProductDetail] = useLazyGetProductAndServiceByIdQuery();

  const formikRef = useRef<FormikProps<FormInvoiceItem>>(null);

  const [systemTaxRate, setSystemTaxRate] = useState<number | null>(
    invoice_item.system_tax_rate ?? null
  );

  const handleSystemTaxRateChange = (rate: number | null) => {
    setSystemTaxRate(rate);
    formikRef.current?.setFieldValue("system_tax_rate", rate);
  };

  const taxCompliance = useLineItemTaxCompliance({
    entityId,
    sellerAddress,
    buyerAddress,
    systemTaxRate,
    onSystemTaxRateChange: handleSystemTaxRateChange,
    initialSalesTaxCode: invoice_item.sales_tax_code || null,
  });

  // Existing items already have a sales tax code selected — kick off the
  // compliance calculation once when the form opens, same as if the user
  // had just picked that code. Ref-guarded so it only fires the first render.
  const hasRunInitialTaxCalculation = useRef(false);
  if (!hasRunInitialTaxCalculation.current && invoice_item.sales_tax_code) {
    hasRunInitialTaxCalculation.current = true;
    void taxCompliance.recalculate();
  }

  const root_category_id = categoryData
    ?.map((category) => category.uuid)
    .join(",");

  const defaultCategoryData = settingsData?.category_data?.filter(
    (item: CategoryData) =>
      item.category_name ===
      (isBillLineItem
        ? OPERATING_EXPENSES_CATEGORY
        : SALES_AND_PRODUCTS_CATEGORY)
  );

  const defaultCategory = defaultCategoryData?.[0]?.category || null;

  const handleFormSubmit = (values: FormInvoiceItem) => {
    const { is_public_bill_item, ...rest } = values;
    const updatedItem = {
      ...rest,
      uuid: rest.uuid || "",
      category_id: rest.category_id || null,
      classification_ids: rest.classification_ids || [],
    };

    onSave?.(updatedItem);
  };

  const initialValues: FormInvoiceItem = {
    uuid: invoice_item.uuid || "",
    quantity: invoice_item.quantity || 1,
    rate: invoice_item.rate || 0,
    description: invoice_item.description || "",
    billing_period_enabled: invoice_item.billing_period_enabled || false,
    billing_period_start_date:
      invoice_item.billing_period_start_date || invoiceDate || null,
    billing_period_end_date: invoice_item.billing_period_end_date || null,
    product_data_id: invoice_item.product_data_id || null,
    price_id: invoice_item.price_id || null,
    category_id:
      invoice_item.category_id ||
      invoice_item.category?.uuid ||
      (isBillLineItem || permission?.blocked
        ? defaultCategory?.uuid || null
        : null),
    category:
      invoice_item.category ||
      (isBillLineItem || permission?.blocked
        ? {
            name: defaultCategory?.name || "",
            uuid: defaultCategory?.uuid || "",
            identifier: "",
          }
        : undefined),
    is_public_bill_item: !showCategory,
    is_new_item: invoice_item.is_new_item || false,
    classification_ids: !invoice_item.classification
      ? []
      : invoice_item.classification.flatMap((group) =>
          group.classes.map((cls) => cls.uuid)
        ),
    tax_rate_id:
      invoice_item.tax_rate_id || invoice_item.tax_rate?.uuid || null,
    product_code: invoice_item.product_code || null,
    system_tax_rate: invoice_item.system_tax_rate ?? null,
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={InvoiceItemValidation}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm }) => {
        const hasDuplicateClassification = checkHasDuplicateClassification({
          values,
          invoice_item,
          existingInvoiceItems,
          allClassifications,
        });

        const isFormValid = !hasDuplicateClassification && values.category_id;

        const missingAddressReasons: string[] = [];
        if (
          showTaxField &&
          values.product_code &&
          !taxCompliance.hasSellerAddress
        ) {
          missingAddressReasons.push("your entity's address");
        }
        const isMissingBuyerAddress =
          showTaxField &&
          Boolean(values.product_code) &&
          !taxCompliance.hasBuyerAddress;
        if (isMissingBuyerAddress) {
          missingAddressReasons.push("the customer's billing address");
        }
        const missingTaxInfoMessage =
          missingAddressReasons.length &&
          !taxCompliance.isRegistrationIncomplete
            ? `We couldn't auto-calculate sales tax because ${missingAddressReasons.join(
                " and "
              )} ${
                missingAddressReasons.length > 1 ? "are" : "is"
              } missing or incomplete. Add it, or enter Tax (%) manually.`
            : null;

        const handleProductSelect = async (
          product: ProductAndServicesDetail
        ) => {
          setFieldValue("product_data_id", product.uuid || null);
          setFieldValue("description", product.name);
          setFieldValue("rate", 0);
          setFieldValue("price_id", null);
          setFieldValue(
            "category_id",
            product.transaction_category_id
              ? product.transaction_category_id
              : defaultCategory?.uuid || null
          );
          setFieldValue(
            "category.name",
            product.transaction_category_id
              ? product.transaction_category_name
              : defaultCategory?.name || ""
          );
          setFieldValue("tax_rate_id", product.default_tax_rate?.uuid || null);
          setFieldValue("product_code", product.sales_tax_code?.uuid || null);
          taxCompliance.selectSalesTaxCode(product.sales_tax_code || null);

          const classificationIds = product.classification
            ? product.classification.flatMap((group) =>
                group.classes.map((cls) => cls.uuid)
              )
            : [];
          setFieldValue("classification_ids", classificationIds);

          if (!product.uuid) return;

          try {
            const productDetail = await fetchProductDetail({
              productId: product.uuid,
              entityId,
              groupId,
            }).unwrap();

            const linkedPrices = productDetail.linked_price_list || [];
            const customerDefaultPrice = customerDefaultPrices.find(
              (cdp) => cdp.product_id === product.uuid
            );
            const defaultPrice =
              customerDefaultPrice?.product_price ||
              linkedPrices.find((price) => price.is_default);

            if (!defaultPrice) return;

            setFieldValue("rate", defaultPrice.price);
            setFieldValue("price_id", defaultPrice.uuid);
          } catch (error) {
            console.error("Failed to prefill default price", error);
          }
        };

        return (
          <Form className="t-w-full">
            <ItemWrapper
              invoiceItem={invoice_item}
              onNewProductCreateSuccess={({ item }) => {
                const defaultPrice = item.linked_price_list?.find(
                  (p) => p.is_default
                );
                setFieldValue("product_data_id", item.uuid || null);
                setFieldValue("description", item.name || "");
                setFieldValue("rate", defaultPrice?.price || 0);
                setFieldValue("price_id", defaultPrice?.uuid || null);
                setFieldValue(
                  "category_id",
                  item.transaction_category_id
                    ? item.transaction_category_id
                    : defaultCategory?.uuid || null
                );
                setFieldValue(
                  "category.name",
                  item.transaction_category_id
                    ? item.transaction_category_name
                    : defaultCategory?.name || ""
                );
                setFieldValue(
                  "tax_rate_id",
                  item.default_tax_rate?.uuid || null
                );
                setFieldValue(
                  "product_code",
                  item.sales_tax_code?.uuid || null
                );
                taxCompliance.selectSalesTaxCode(item.sales_tax_code || null);
                setFieldValue(
                  "classification_ids",
                  item.classification?.flatMap((group) =>
                    group.classes.map((cls) => cls.uuid)
                  ) ?? []
                );
              }}
            >
              {({
                isCustomInput,
                createNewProduct,
                addNewProductAndServicesModal,
              }) => (
                <div className="t-flex t-flex-col t-gap-4">
                  <div className="t-flex t-flex-col t-gap-3">
                    <div
                      className={classNames(
                        "t-grid t-gap-3 t-items-start t-min-w-0 sm:t-grid-cols-2",
                        {
                          "t-grid-cols-[1.5fr_0.5fr_1fr]": true,
                        }
                      )}
                    >
                      <div className="!t-m-0 !t-p-0 t-min-w-0 sm:t-col-span-2">
                        <div className="t-flex t-items-center t-gap-1 t-mb-1">
                          <Label
                            required
                            htmlFor={
                              permission?.blocked || isCustomInput
                                ? `description`
                                : `product_data_id`
                            }
                            className="t-text-body-sm t-text-text-60 !t-pb-0"
                          >
                            Item
                          </Label>
                        </div>
                        <InvoiceItemSelector
                          createNewProduct={(inputValue) => {
                            createNewProduct(inputValue);
                            setFieldValue("description", inputValue);
                            setFieldValue("product_data_id", null);
                            setFieldValue("price_id", null);
                            setFieldValue(
                              "category_id",
                              defaultCategory?.uuid || null
                            );
                            setFieldValue(
                              "category.name",
                              defaultCategory?.name || ""
                            );
                            setFieldValue("classification_ids", []);
                            setFieldValue("product_code", null);
                            taxCompliance.selectSalesTaxCode(null);
                          }}
                          invoiceItem={values}
                          addNewProduct={addNewProductAndServicesModal}
                          onSelect={handleProductSelect}
                          showTextField={showTextField}
                          size="regular"
                        />
                      </div>

                      <div className="t-min-w-0">
                        <NumericInput
                          storeNumeric
                          fieldProps={{
                            name: "quantity",
                          }}
                          numericProps={{
                            customSize: "regular",
                          }}
                          label="Quantity"
                          required
                        />
                      </div>

                      <div className="t-min-w-0">
                        <PriceDropdown
                          key={values.product_data_id}
                          productId={values.product_data_id}
                          groupId={groupId}
                          entityId={entityId}
                          customerId={customerId}
                          currency={currency}
                          name="rate"
                          size="regular"
                        />
                      </div>
                    </div>

                    <SetDefaultPriceBanner
                      productId={values.product_data_id}
                      priceId={values.price_id}
                      rate={values.rate}
                      customerId={customerId}
                      entityId={entityId}
                      groupId={groupId}
                    />
                    <div className="t-flex t-flex-col t-gap-3">
                      <CheckboxField
                        name="billing_period_enabled"
                        checkboxProps={{
                          label: (
                            <span className="t-text-body-sm">
                              Add billing period
                            </span>
                          ),
                        }}
                      />

                      {values.billing_period_enabled && (
                        <div className="t-flex t-gap-3 t-items-center sm:t-gap-2">
                          <div className="t-flex-1 sm:t-min-w-0">
                            <DateField
                              name="billing_period_start_date"
                              placeholder="Select start date"
                              customSize={isMobile ? "small" : "regular"}
                              showErrorOnceTouched
                              portalId="billing-start-0"
                              required
                              rightComponent={
                                <span className="t-text-text-30">
                                  <CalendarIcon />
                                </span>
                              }
                            />
                          </div>

                          <span className="t-text-text-30 sm:t-shrink-0">
                            <RightArrow />
                          </span>

                          <div className="t-flex-1 sm:t-min-w-0">
                            <DateField
                              name="billing_period_end_date"
                              placeholder="Select end date"
                              customSize={isMobile ? "small" : "regular"}
                              showErrorOnceTouched
                              portalId="billing-end-0"
                              required
                              minDate={
                                values.billing_period_start_date
                                  ? new Date(values.billing_period_start_date)
                                  : undefined
                              }
                              rightComponent={
                                <span className="t-text-text-30">
                                  <CalendarIcon />
                                </span>
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {showCategory && (
                      <div
                        className={classNames(
                          "t-grid t-gap-3 t-items-end sm:t-grid-cols-1",
                          {
                            "t-grid-cols-2": true,
                          }
                        )}
                      >
                        <div>
                          <ConditionalToolTip
                            condition={
                              Boolean(values.product_data_id) && (
                                <div className="t-w-64">
                                  The income account can be updated directly
                                  from My Products.
                                </div>
                              )
                            }
                            side="bottom"
                            sideOffset={4}
                            align="end"
                          >
                            <div>
                              <CategorySelector
                                hiddenCategoryTypes={[]}
                                size="regular"
                                name="category_id"
                                label="Category"
                                category={values.category_id || ""}
                                isDisabled={Boolean(values.product_data_id)}
                                root_category_id={root_category_id}
                                hideAction={true}
                                required={true}
                                onChange={(value) => {
                                  setFieldValue(
                                    "category.name",
                                    value ? value.name : ""
                                  );
                                }}
                              />
                            </div>
                          </ConditionalToolTip>
                        </div>

                        <div>
                          <ClassificationAssignment
                            entityId={entityId}
                            assignedClassificationIds={
                              values.classification_ids || []
                            }
                            onAssignClass={(
                              classUuid,
                              _groupUuid,
                              existingClassToRemove
                            ) => {
                              const currentIds =
                                values.classification_ids || [];
                              setFieldValue("classification_ids", [
                                ...currentIds.filter(
                                  (id) => id !== existingClassToRemove
                                ),
                                classUuid,
                              ]);
                            }}
                            onRemoveClass={(classUuid) => {
                              const currentIds =
                                values.classification_ids || [];
                              setFieldValue(
                                "classification_ids",
                                currentIds.filter((id) => id !== classUuid)
                              );
                            }}
                            label="Classification"
                            readOnly={!isCustomInput}
                            maxChips={1}
                            blockedUI={
                              <Combobox
                                label={
                                  <span className="t-flex t-items-center t-gap-1">
                                    Classification{" "}
                                    <DiamondPurple height={13} width={14} />
                                  </span>
                                }
                                isMulti
                                size="regular"
                                placeholder="Add group and classes"
                                menuPortalTarget={document.body}
                                isDisabled
                                value={[]}
                                options={[]}
                              />
                            }
                          />
                        </div>
                      </div>
                    )}
                    {showTaxField && (
                      <div className="t-flex t-gap-3 sm:t-flex-col">
                        <div className="t-w-1/2 sm:t-w-full">
                          <TaxRateCombobox
                            name="tax_rate_id"
                            systemSuggestedRate={values.system_tax_rate}
                          />
                        </div>
                        <div className="t-w-1/2 sm:t-w-full">
                          <ConditionalToolTip
                            condition={
                              Boolean(values.product_data_id) && (
                                <div className="t-w-64">
                                  Update the sales tax code from My Products.
                                </div>
                              )
                            }
                            side="bottom"
                            sideOffset={4}
                            align="end"
                          >
                            <div>
                              <SalesTaxCodeCombobox
                                name="product_code"
                                isDisabled={Boolean(values.product_data_id)}
                                onSelect={taxCompliance.selectSalesTaxCode}
                              />
                            </div>
                          </ConditionalToolTip>
                        </div>
                      </div>
                    )}
                    {showTaxField && (
                      <LineItemTaxCompliance
                        missingTaxInfoMessage={missingTaxInfoMessage}
                        hasSystemTaxRate={
                          values.system_tax_rate !== null &&
                          values.system_tax_rate !== undefined
                        }
                        onAddCustomerAddress={
                          isMissingBuyerAddress &&
                          customerId &&
                          onEditCustomerAddress
                            ? () => onEditCustomerAddress(customerId)
                            : undefined
                        }
                      />
                    )}
                  </div>

                  {hasDuplicateClassification && (
                    <Banner
                      type="warning"
                      title="Items with the same category cannot have different classes from the same group."
                      description=""
                    />
                  )}

                  <div className="t-flex t-flex-row t-gap-2">
                    <Button
                      customType="primary_outlined"
                      isLoading={isUpdating}
                      disabled={isUpdating || !isFormValid}
                      onClick={() => submitForm()}
                    >
                      {values.uuid ? "Update item" : " Add item"}
                    </Button>
                    <Button
                      type="button"
                      customType="secondary"
                      onClick={closeModal}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </ItemWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
